import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { environment } from '@environements';
import { Pagination } from '@interfaces/index';
import { catchError, map, Observable, of } from 'rxjs';
import { BACKGROUND_REQUEST } from '@interceptors/http-context';
import { DEFAULT_PAGINATION } from './types';
import { GetNotificationsParams, Notification } from './interfaces/notification.interface';

export type {
  Notification,
  NotificationSeverity,
  NotificationCategory,
  GetNotificationsParams,
} from './interfaces/notification.interface';

@Service()
export class NotificationService {
  private http = inject(HttpClient);
  private base = `${environment.SAAS_SERVICE}/notifications`;

  getAll(query: GetNotificationsParams): Observable<Pagination<Notification>> {
    let params = new HttpParams().set('page', query.page).set('perPage', query.perPage);
    if (query.category) {
      params = params.set('category', query.category);
    }
    if (query.open) {
      params = params.set('open', 'true');
    }
    return this.http
      .get<Pagination<Notification>>(this.base, { params })
      .pipe(catchError(() => of({ ...DEFAULT_PAGINATION, results: [] as Notification[] })));
  }

  /**
   * One page of the open alarms. Unlike `getAll`, a failure is thrown rather than
   * turned into an empty page, so the caller can tell "no alarms" from "couldn't
   * load" (the dashboard panel must not clear its list on a failed live re-read).
   * `background` keeps the generic error toast quiet for those re-reads.
   */
  getOpenAlarms(
    page: number,
    perPage: number,
    background = false,
  ): Observable<Pagination<Notification>> {
    const params = new HttpParams()
      .set('page', page)
      .set('perPage', perPage)
      .set('category', 'alarm')
      .set('open', 'true');
    return this.http.get<Pagination<Notification>>(this.base, {
      params,
      context: new HttpContext().set(BACKGROUND_REQUEST, background),
    });
  }

  getUnreadCount(): Observable<number> {
    return this.http
      .get<{ count: number }>(`${this.base}/unread-count`, {
        context: new HttpContext().set(BACKGROUND_REQUEST, true),
      })
      .pipe(map((res) => res.count));
  }

  markRead(id: string): Observable<boolean> {
    return this.http.post<void>(`${this.base}/${id}/read`, {}).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  markAllRead(): Observable<boolean> {
    return this.http.post<void>(`${this.base}/read-all`, {}).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }
}
